import { useContext, useState, useRef } from 'react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { Button } from '../ui/button';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { LangContext } from '@/context/LangProvider';
import CreateFromScratch from '../project-creation/CreateFromScratch';
import CreateWithAi from '../project-creation/CreateWithAi';
import ChooseProjectCreationType from '../project-creation/ChooseProjectCreationType';
import CreateFromTemplate from '../project-creation/CreateFromTemplate';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import FolderPlusIcon from '../icons/FolderPlusIcon';
import { ECompanyThemeName } from '@/enums';
import ModalToastContainer from '../ui/ModalToastContainer';
import type { ProjectType } from '@/types';

function CreateProjectModal() {
  const { lang } = useContext(LangContext);
  const [openCreatingProject, setOpenCreatingProject] = useState(false);
  const [creationType, setCreationType] = useState('');
  // TELV1-186: track the selected project type so it flows through to
  // the create-project API call. Defaults to 'interview' (the original
  // behaviour for all pre-existing code paths).
  const [projectType, setProjectType] = useState<ProjectType>('interview');
  const { isUserOwner, isUserEditor } = useWorkspaceRoles();
  const { themeName } = useTheme();
  const modalRef = useRef<HTMLDivElement>(null);

  const closeModalAndRemoveData = () => {
    setOpenCreatingProject(false);
    setCreationType('');
    setProjectType('interview');
  };

  return (
    <>
      {/* trigger */}
      <Button
        disabled={!(isUserOwner || isUserEditor)}
        type="button"
        variant="default"
        className="flex gap-2 md900:w-full"
        onClick={() => setOpenCreatingProject(true)}
      >
        <FolderPlusIcon className="size-5" />
        {themeName === ECompanyThemeName.EMPATHY
          ? lang.get('msg.createNewTellet_empathy')
          : lang.get('msg.createNewTellet')}
      </Button>
      {/* modal */}
      <AlertDialog open={openCreatingProject}>
        <AlertDialogContent
          className="w-full max-w-[90%] h-[85%] text-dark-text flex"
          ref={modalRef}
        >
          {/* picking choice */}
          {creationType === '' && (
            <ChooseProjectCreationType
              setCreationType={setCreationType}
              setProjectType={setProjectType}
              closeModalAndRemoveData={closeModalAndRemoveData}
            />
          )}
          {/* if scratch (both interview and embed go through this flow) */}
          {creationType === 'scratch' && (
            <CreateFromScratch
              goBack={() => setCreationType('')}
              closeModalAndRemoveData={closeModalAndRemoveData}
              projectType={projectType}
            />
          )}
          {/* if template */}
          {creationType === 'template' && (
            <CreateFromTemplate
              setCreationType={setCreationType}
              closeModalAndRemoveData={closeModalAndRemoveData}
            />
          )}
          {/* if ai */}
          {creationType === 'ai' && (
            <CreateWithAi
              setCreationType={setCreationType}
              closeModalAndRemoveData={closeModalAndRemoveData}
            />
          )}
          <ModalToastContainer targetRef={modalRef} />
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default CreateProjectModal;
